import { toBusinessDate } from '@paguz/contracts';
import { Badge, cn, PaguzLogo } from '@paguz/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import {
  Building2Icon,
  CalendarClockIcon,
  ChevronDownIcon,
  HistoryIcon,
  LockIcon,
  LogOutIcon,
  SearchIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, queryKeys } from '../api/endpoints.ts';
import { appConfig } from '../config.ts';
import { formatBusinessDate, formatDateTime } from '../lib/format.ts';
import { can, ROLE_LABELS } from '../lib/rbac.ts';
import { sessionStore, useSession } from '../lib/session.ts';
import { navFor, resolveSearch, type NavItem } from './nav.ts';

export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (!session) void navigate({ to: '/login', search: { redirect: undefined } });
  }, [session, navigate]);

  if (!session) return null;
  const items = navFor(session.user.roles);
  const groups = [...new Set(items.map((i) => i.group))];

  return (
    <div className="flex min-h-dvh bg-background">
      <aside className="hidden w-64 shrink-0 bg-inverse text-inverse-foreground lg:block">
        <div className="sticky top-0 flex h-dvh flex-col">
          <div className="flex h-16 items-center gap-2 px-5">
            <PaguzLogo tone="white" className="h-7 w-auto" />
            <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs font-semibold tracking-wide">
              Consola
            </span>
          </div>
          <nav aria-label="Secciones" className="flex-1 overflow-y-auto px-3 py-2">
            {groups.map((group) => (
              <div key={group} className="mb-4">
                <p className="px-3 pb-1 text-[0.6875rem] font-semibold tracking-wider text-inverse-muted-foreground uppercase">
                  {group}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {items
                    .filter((i) => i.group === group)
                    .map((item) => (
                      <li key={item.to}>
                        <SideLink item={item} />
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </nav>
          <div className="border-t border-white/10 px-5 py-4 text-xs text-inverse-muted-foreground">
            <p className="font-semibold text-inverse-foreground">{appConfig.tenant.displayName}</p>
            <p className="mt-0.5 flex items-center gap-1">
              powered by <PaguzLogo tone="white" className="h-3.5 w-auto" />
            </p>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <Header />
        <MobileNav items={items} />
        <main id="contenido" className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 xl:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SideLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' }}
      className="group flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium text-inverse-foreground/85 transition-colors hover:bg-white/10 hover:text-inverse-foreground focus-visible:ring-[3px] focus-visible:ring-violet-light/60 focus-visible:outline-none data-[status=active]:bg-white/15 data-[status=active]:text-inverse-foreground"
    >
      <Icon className="size-[1.125rem] shrink-0 opacity-80 group-data-[status=active]:text-violet-light group-data-[status=active]:opacity-100" />
      {item.label}
    </Link>
  );
}

/** En tablet (< 1024 px) la barra lateral pasa a una fila de pestañas. */
function MobileNav({ items }: { items: NavItem[] }) {
  return (
    <nav
      aria-label="Secciones"
      className="flex gap-1 overflow-x-auto border-b border-border bg-card px-4 py-2 lg:hidden"
    >
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          activeOptions={{ exact: item.to === '/' }}
          className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent data-[status=active]:bg-highlight-soft data-[status=active]:text-highlight-soft-foreground"
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

function Header() {
  const session = useSession();
  const roles = session?.user.roles ?? [];
  const clock = useQuery({
    queryKey: queryKeys.clock,
    queryFn: api.getClock,
    enabled: can(roles, 'demo:operate') || can(roles, 'dashboard:read'),
    staleTime: 30_000,
  });

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-card/95 px-6 backdrop-blur xl:px-10">
      <div className="lg:hidden">
        <PaguzLogo className="h-6 w-auto" />
      </div>
      <TenantChip />
      <GlobalSearch />
      <div className="ml-auto flex items-center gap-3">
        {clock.data && (
          <Link
            to="/demo"
            hash="reloj"
            className={cn(
              'hidden shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap md:inline-flex',
              clock.data.mode === 'SIMULATED'
                ? 'border-warning/40 bg-warning-soft text-warning-soft-foreground'
                : 'border-border text-muted-foreground',
            )}
            title="Reloj de demo"
          >
            <CalendarClockIcon className="size-4" />
            {clock.data.mode === 'SIMULATED'
              ? `Simulado +${clock.data.offsetDays} d · ${formatBusinessDate(toBusinessDate(new Date(clock.data.now)))}`
              : 'Tiempo real'}
          </Link>
        )}
        <UserMenu />
      </div>
    </header>
  );
}

function TenantChip() {
  return (
    <button
      type="button"
      disabled
      title="Tenant fijo en el demo (solo un ADMIN de plataforma puede cambiarlo)"
      className="hidden h-10 shrink-0 items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm font-semibold whitespace-nowrap text-foreground disabled:cursor-default md:inline-flex"
    >
      <Building2Icon className="size-4 text-primary" />
      {appConfig.tenant.displayName}
      <Badge variant="highlight">{appConfig.tenant.environment}</Badge>
      <LockIcon className="size-3.5 text-muted-foreground" aria-label="Fijo" />
    </button>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  return (
    <form
      role="search"
      className="relative w-full max-w-md"
      onSubmit={(event) => {
        event.preventDefault();
        const target = resolveSearch(value);
        if (!target) return;
        setValue('');
        switch (target.kind) {
          case 'payment':
            void navigate({
              to: '/transacciones/$paymentIntentId',
              params: { paymentIntentId: target.id },
            });
            return;
          case 'line':
            void navigate({
              to: '/lineas/$lineId',
              params: { lineId: target.id },
              search: { tab: undefined },
            });
            return;
          case 'customer':
            void navigate({ to: '/clientes/$customerId', params: { customerId: target.id } });
            return;
          case 'application':
            void navigate({
              to: '/expedientes/$applicationId',
              params: { applicationId: target.id },
            });
            return;
          case 'customers':
            void navigate({ to: '/clientes', search: { q: target.q } });
            return;
        }
      }}
    >
      <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        aria-label="Buscar"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        placeholder="Buscar pi_…, ln_…, cus_…, documento o celular"
        className="h-10 w-full rounded-lg border border-input bg-background pr-3 pl-9 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
      />
    </form>
  );
}

function UserMenu() {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!session) return null;
  const { user } = session;
  const initials = user.fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0))
    .join('');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="flex h-10 cursor-pointer items-center gap-2 rounded-full border border-border bg-background pr-3 pl-1 text-sm font-semibold hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
          {initials}
        </span>
        <span className="hidden max-w-40 truncate xl:inline">{user.fullName}</span>
        <ChevronDownIcon className="size-4 text-muted-foreground" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-12 right-0 z-40 w-72 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-xl"
        >
          <div className="px-3 py-2">
            <p className="font-semibold">{user.fullName}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {user.roles.map((role) => (
                <Badge key={role} variant="secondary">
                  {ROLE_LABELS[role]}
                </Badge>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Sesión desde {formatDateTime(session.loginAt)} · vence{' '}
              {formatDateTime(session.expiresAt)}
            </p>
          </div>
          <div className="my-1 h-px bg-border" />
          <Link
            to="/mi-actividad"
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
            className="flex h-10 items-center gap-2 rounded-lg px-3 text-sm hover:bg-accent"
          >
            <HistoryIcon className="size-4" /> Mi actividad en esta sesión
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              sessionStore.end();
              queryClient.clear();
              void navigate({ to: '/login', search: { redirect: undefined } });
            }}
            className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg px-3 text-left text-sm text-destructive hover:bg-danger-soft"
          >
            <LogOutIcon className="size-4" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
